// This file contains the API slices for different versions of the API and backend ressources.
// Each slice is responsible for handling requests to a specific API version or backend resource.
// FUTURE: Consider refactoring to a more modular structure if the number of slices grows significantly.
// components

import { resolveAccountId } from '@/redux/accountContext';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { GraphQLClient } from 'graphql-request';

const prepareDaluxAccountHeader = (headers) => {
  const accountId = resolveAccountId();

  if (accountId) {
    headers.set('x-account-id', String(accountId));
  }

  return headers;
};

export const baseQueryWithAuthHandling = (options) => {
  const baseQuery = fetchBaseQuery(options);

  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
      const errorType = result.error?.data?.error;
      if (errorType === 'session_expired' || errorType === 'invalid_grant') {
        // invalid_grant means the token cannot be silently refreshed — redirect to
        // full login to avoid an infinite silent-login -> 401 loop.
        window.location.href = '/auth/login';
      }
    }

    return result;
  };
};
// ============= Handles API v1 requests ============= //
export const apiSlice = createApi({
  reducerPath: 'hostApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/api/v1',
    credentials: 'include',
    prepareHeaders: (headers) => {
      // No manual Authorization headers
      return headers;
    },
  }),
  endpoints: () => ({}),
});

// ============= Handles API v2 requests ============= //
export const apiSliceV2 = createApi({
  reducerPath: 'recalcV2ApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/api/v2',
    credentials: 'include',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['RecalcV2'],
  endpoints: () => ({}),
});

// ============= Handles Admin API requests ============= //
export const adminApiSlice = createApi({
  reducerPath: 'adminApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/api/admin',
    credentials: 'include',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['AdminAPI'],
  endpoints: () => ({}),
});

// ============= Handles generated API v2 requests ============= //
// Uses baseUrl '/api' so generated paths like '/v2/Gateways' resolve correctly
// to '/api/v2/Gateways' without duplicating the /v2 prefix.
export const apiSliceV2Generated = createApi({
  reducerPath: 'recalcV2GeneratedApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/api',
    credentials: 'include',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['RecalcV2Generated'],
  endpoints: () => ({}),
});

// ============= Handles Express API requests ============= //
export const expressApiSlice = createApi({
  reducerPath: 'expressApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/express',
    prepareHeaders: (headers) => {
      return headers;
    },
    credentials: 'include',
  }),
  // tagTypes: [''],
  endpoints: () => ({}),
});

// ============= Handles Energy Analysis API requests ============= //
export const energyAnalysisApiSlice = createApi({
  reducerPath: 'energyAnalysisApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/energy-analysis',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: [
    'EnergyAnalysis',
    'PortfolioAnalysis',
    'PortfolioAnalysisSubscription',
    'PortfolioEnergyGroupGroup',
  ],
  endpoints: () => ({}),
});

// ============= Handles Service System Monitoring API requests ============= //
export const serviceSystemMonitoringApiSlice = createApi({
  reducerPath: 'serviceSystemMonitoringApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/service-system-monitoring',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['ServiceSystemMonitoring'],
  endpoints: () => ({}),
});

// ============= Handles Subscription Manager API requests ============= //
export const subscriptionManagerApiSlice = createApi({
  reducerPath: 'subscriptionManagerApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/subscription-manager',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['Subscription', 'Building'],
  endpoints: () => ({}),
});
export const datahubIntegrationApiSlice = createApi({
  reducerPath: 'datahubIntegrationApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/datahub-integration',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['DatahubIntegration'],
  endpoints: () => ({}),
});

// ============= Handles Data Integrations API requests ============= //
export const dataIntegrationsApiSlice = createApi({
  reducerPath: 'dataIntegrationsApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/data-integrations',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['Eforsyning'],
  endpoints: () => ({}),
});

export const cvrBaseApiSlice = createApi({
  reducerPath: 'cvrApi',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: 'https://datacvr.virk.dk/gateway/virksomhed/',
  }),
  endpoints: () => ({}),
});

// ============= Handles Energy Savings API requests ============= /
export const energySavingsApiSlice = createApi({
  reducerPath: 'energySavingsApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/energy-savings',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['EnergySaving'],
  endpoints: () => ({}),
});

// ============= Handles Adressevælger API requests ============= //
// Used for fuzzy address suggestions as the user types. Requests are routed through
// the BFF (/adressevaelger), which always overwrites the `token` query param with the
// real, server-side-only token before forwarding - see bff/server.js.
export const adressevaelgerApiSlice = createApi({
  reducerPath: 'adressevaelgerApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/adressevaelger',
  }),
  endpoints: () => ({}),
});

// `graphql-request` falls back to its own bundled `cross-fetch` (which uses
// `node-fetch` outside a browser) unless an explicit `fetch` implementation
// is provided. That fallback rejects the relative URLs below with
// "Only absolute URLs are supported" in Node-based test environments (and
// under SSR). Passing `globalThis.fetch` explicitly makes it use the same
// fetch implementation (and relative-URL handling) as the rest of the app.
const darGraphqlClient = new GraphQLClient('/dar-graphql', {
  credentials: 'include',
  fetch: (...args) => globalThis.fetch(...args),
});

const matGraphqlClient = new GraphQLClient('/mat-graphql', {
  credentials: 'include',
  fetch: (...args) => globalThis.fetch(...args),
});

// ============= Handles DAR GraphQL API requests ============= //
export const darGraphqlApiSlice = createApi({
  reducerPath: 'darGraphqlApiSlice',
  baseQuery: graphqlRequestBaseQuery({
    client: darGraphqlClient,
  }),
  endpoints: () => ({}),
});

// ============= Handles MAT GraphQL API requests ============= //
export const matGraphqlApiSlice = createApi({
  reducerPath: 'matGraphqlApiSlice',
  baseQuery: graphqlRequestBaseQuery({
    client: matGraphqlClient,
  }),
  endpoints: () => ({}),
});

// ============= Handles Power BI API requests ============= //
export const powerbiApiSlice = createApi({
  reducerPath: 'powerbiApiSlice',
  baseQuery: fetchBaseQuery({
    baseUrl: '/powerbi',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  tagTypes: ['PowerBI'],
  endpoints: () => ({}),
});

// Authserver Proxy
export const authServerApiSlice = createApi({
  reducerPath: 'authServerApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/auth',
    credentials: 'include',
  }),
  tagTypes: ['AuthServer'],
  endpoints: () => ({}),
});

export const daluxfmServerApiSlice = createApi({
  reducerPath: 'daluxfmServerApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/daluxfm',
    credentials: 'include',
    prepareHeaders: (headers, { getState }) =>
      prepareDaluxAccountHeader(headers, getState),
  }),
  tagTypes: ['DaluxFMServer'],
  endpoints: () => ({}),
});

// ============= Handles Dalux Integration (BFF local storage) requests ============= //
export const daluxIntegrationApiSlice = createApi({
  reducerPath: 'daluxIntegrationApiSlice',
  baseQuery: baseQueryWithAuthHandling({
    baseUrl: '/dalux-integration',
    credentials: 'include',
    prepareHeaders: (headers, { getState }) =>
      prepareDaluxAccountHeader(headers, getState),
  }),
  tagTypes: ['DaluxApiKey', 'DaluxAssociation'],
  endpoints: () => ({}),
});
